import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GitMerge } from 'lucide-react'
import { api, messageErreur } from '../../lib/api'
import type { Donnee, PatientResume } from '../../lib/types'
import { Chargement, Message, Modale, Pastille } from '../base'
import SelecteurPatient from '../SelecteurPatient'
import * as f from '../../lib/format'
import { CATEGORIES_DONNEE } from '@doc/shared'

type Apercu = {
  cible: PatientResume & { compteurs: Record<string, number> }
  source: PatientResume & { compteurs: Record<string, number> }
  differences: { champ: string; cible: unknown; source: unknown }[]
  doublons: { categorie: string; libelle: string; cible: Donnee; source: Donnee }[]
  aDeplacer: Record<string, number>
}

const ETIQUETTES: Record<string, string> = {
  nom: 'Nom',
  nomNaissance: 'Nom de naissance',
  prenom: 'Prénom',
  dateNaissance: 'Date de naissance',
  sexe: 'Sexe',
  telephone: 'Téléphone',
  email: 'E-mail',
  adresse: 'Adresse',
  notes: 'Notes',
}

const CHOSES = {
  venues: 'venues',
  documents: 'documents',
  donnees: 'données médicales',
  bilans: 'bilans',
  alertes: 'alertes',
}

/**
 * Fusion de deux dossiers de la même personne. Rien n'est écrit avant
 * confirmation : on montre d'abord ce qui bouge, ce qui diffère et ce qui
 * restera à trancher (critère d'acceptation 16).
 */
export default function Fusion({
  patientId,
  fermer,
}: {
  patientId: string
  fermer: () => void
}) {
  const client = useQueryClient()
  const [autre, setAutre] = useState<PatientResume | null>(null)
  const [choix, setChoix] = useState<Record<string, 'source' | 'cible'>>({})
  const [erreur, setErreur] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['fusion', patientId, autre?.id],
    queryFn: () => api<Apercu>(`/api/patients/${patientId}/fusion/apercu?autreId=${autre!.id}`),
    enabled: Boolean(autre),
  })

  const fusionner = useMutation({
    mutationFn: () =>
      api('/api/patients/fusion', {
        methode: 'POST',
        corps: { sourceId: autre!.id, cibleId: patientId, identite: choix },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      client.invalidateQueries({ queryKey: ['patients'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  if (!autre) {
    return (
      <SelecteurPatient
        titre="Fusionner avec quel dossier ?"
        sousTitre="Le dossier choisi sera absorbé dans celui-ci. Aucun identifiant ne disparaît."
        fermer={fermer}
        choisir={setAutre}
      />
    )
  }

  return (
    <Modale
      titre="Fusionner deux dossiers"
      sousTitre="Vérifie ce qui va bouger avant de confirmer."
      large
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret mr-auto" onClick={() => setAutre(null)}>
            Choisir un autre dossier
          </button>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => fusionner.mutate()}
            disabled={fusionner.isPending || isLoading}
          >
            <GitMerge className="h-4 w-4" />
            Fusionner
          </button>
        </>
      }
    >
      {isLoading || !data ? (
        <Chargement />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-bleu-50 px-4 py-3">
              <div className="text-[11.5px] font-medium tracking-wide text-bleu-700 uppercase">
                Dossier conservé
              </div>
              <p className="mt-1 text-[14px] font-medium">{data.cible.affichage}</p>
              <p className="text-[12px] text-brume">{data.cible.reference}</p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="text-[11.5px] font-medium tracking-wide text-brume uppercase">
                Dossier absorbé
              </div>
              <p className="mt-1 text-[14px] font-medium">{data.source.affichage}</p>
              <p className="text-[12px] text-brume">{data.source.reference}</p>
            </div>
          </div>

          <div>
            <h3 className="mb-1.5 text-[13px] font-semibold">Ce qui va être déplacé</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CHOSES).map(([cle, libelle]) => (
                <Pastille key={cle} ton={data.aDeplacer[cle] ? 'bleu' : 'neutre'}>
                  {data.aDeplacer[cle] ?? 0} {libelle}
                </Pastille>
              ))}
            </div>
            <p className="mt-1.5 text-[12px] text-brume">
              Les sources et l’historique suivent les données : rien n’est perdu.
            </p>
          </div>

          {data.differences.length > 0 && (
            <div>
              <h3 className="mb-1.5 text-[13px] font-semibold">
                Identité : choisir champ par champ
              </h3>
              <ul className="flex flex-col gap-1.5">
                {data.differences.map((d) => (
                  <li
                    key={d.champ}
                    className="grid grid-cols-[130px_1fr_1fr] items-center gap-2 text-[13px]"
                  >
                    <span className="text-brume">{ETIQUETTES[d.champ] ?? d.champ}</span>
                    <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                      <input
                        type="radio"
                        name={`champ-${d.champ}`}
                        className="h-3.5 w-3.5 accent-[var(--color-bleu-600)]"
                        checked={(choix[d.champ] ?? 'cible') === 'cible'}
                        onChange={() => setChoix((c) => ({ ...c, [d.champ]: 'cible' }))}
                      />
                      <span className="truncate">{valeurLisible(d.champ, d.cible)}</span>
                    </label>
                    <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                      <input
                        type="radio"
                        name={`champ-${d.champ}`}
                        className="h-3.5 w-3.5 accent-[var(--color-bleu-600)]"
                        checked={choix[d.champ] === 'source'}
                        onChange={() => setChoix((c) => ({ ...c, [d.champ]: 'source' }))}
                      />
                      <span className="truncate">{valeurLisible(d.champ, d.source)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.doublons.length > 0 && (
            <Message ton="attention">
              <p className="font-medium">
                {data.doublons.length} information{data.doublons.length > 1 ? 's' : ''} présente
                {data.doublons.length > 1 ? 's' : ''} dans les deux dossiers :
              </p>
              <ul className="mt-1 list-disc pl-4">
                {data.doublons.map((d) => (
                  <li key={`${d.categorie}-${d.libelle}`}>
                    {CATEGORIES_DONNEE[d.categorie as keyof typeof CATEGORIES_DONNEE]} :{' '}
                    {d.libelle}
                  </li>
                ))}
              </ul>
              <p className="mt-1">
                Elles ne seront pas supprimées. Un désaccord sera ouvert pour chacune, à trancher
                après la fusion.
              </p>
            </Message>
          )}

          {erreur && <Message ton="critique">{erreur}</Message>}
        </div>
      )}
    </Modale>
  )
}

function valeurLisible(champ: string, valeur: unknown): string {
  if (valeur === null || valeur === undefined || valeur === '') return '— vide —'
  if (champ === 'dateNaissance') return f.dateCourte(String(valeur))
  return String(valeur)
}
